import React from 'react';
import { LuCar, LuChevronRight } from 'react-icons/lu';
import './SavedVehiclePicker.less';

/**
 * SavedVehiclePicker — signed-in "Choose a vehicle" section on the
 * Confirm & Pay page.
 *
 * Pure presentational: the ChargeSetup screen owns the selection
 * state and decides whether to route through PIN verification. This
 * component just renders the saved vehicles as radio-selectable
 * cards and offers a "Use another vehicle" opt-out.
 *
 * Consumes the user-safe DTO from `GET /api/vehicles/mine`:
 *   { id, licensePlate, plateNormalized, displayName?, make?,
 *     model?, year?, color?, status, billingMode }.
 * Never renders PIN, PIN hash, ownership IDs, or any admin field —
 * the DTO does not expose them and the component does not consume
 * them.
 *
 * Rendering rules (kept in sync with ChargeSetup):
 *   - 0 vehicles: caller does not mount this component (only the
 *     manual plate field shows).
 *   - 1 vehicle: caller preselects it; the picker shows the one
 *     card as selected plus the "Use another vehicle" button.
 *   - 2+ vehicles: no default selection; user chooses one.
 *   - After selection: the manual plate field is hidden. The caller
 *     re-shows it when the user clicks "Use another vehicle".
 */
function composeMakeModel(vehicle) {
  const parts = [vehicle.make, vehicle.model].map((s) =>
    typeof s === 'string' ? s.trim() : '',
  );
  const makeModel = parts.filter(Boolean).join(' ').trim();
  const yearStr =
    vehicle.year != null && vehicle.year !== ''
      ? String(vehicle.year).trim()
      : '';
  return [makeModel, yearStr].filter(Boolean).join(' · ') || null;
}

function displayPlate(vehicle) {
  return vehicle.licensePlate || vehicle.plateNormalized || '—';
}

function displayName(vehicle) {
  const raw = typeof vehicle.displayName === 'string' ? vehicle.displayName.trim() : '';
  return raw || null;
}

export default function SavedVehiclePicker({
  vehicles,
  selectedVehicleId,
  onSelect,
  onUseAnother,
  disabled = false,
}) {
  if (!Array.isArray(vehicles) || vehicles.length === 0) {
    return null;
  }

  return (
    <section
      className="gc-saved-vehicle-picker"
      aria-labelledby="gc-saved-vehicle-picker-heading"
    >
      <h3
        id="gc-saved-vehicle-picker-heading"
        className="gc-saved-vehicle-picker-heading"
      >
        Choose a vehicle
      </h3>

      <ul className="gc-saved-vehicle-picker-list" role="radiogroup">
        {vehicles.map((v) => {
          const selected = v.id === selectedVehicleId;
          const plate = displayPlate(v);
          const name = displayName(v);
          const secondary = composeMakeModel(v);
          return (
            <li key={v.id}>
              <button
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={disabled}
                className={`gc-saved-vehicle-option${selected ? ' is-selected' : ''}`}
                onClick={() => onSelect?.(v)}
              >
                <span className="gc-saved-vehicle-option-icon" aria-hidden="true">
                  <LuCar />
                </span>
                <span className="gc-saved-vehicle-option-text">
                  <span className="gc-saved-vehicle-option-plate">{plate}</span>
                  {name && (
                    <span className="gc-saved-vehicle-option-name">{name}</span>
                  )}
                  {secondary && (
                    <span className="gc-saved-vehicle-option-meta">{secondary}</span>
                  )}
                </span>
                <span
                  className="gc-saved-vehicle-option-check"
                  aria-hidden="true"
                >
                  <LuChevronRight />
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      <button
        type="button"
        className="gc-saved-vehicle-picker-alt"
        onClick={() => onUseAnother?.()}
        disabled={disabled}
      >
        Use another vehicle
      </button>
    </section>
  );
}
